import "./production.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import SummaryPrintableComponent from "../../components/printableComponent/productionSummaryPrintableComponent";
import LabelsPrintableComponent from "../../components/printableLabelsComponent/productionLabelsPrintableComponent";
import ProductionLineTable from "./ProductionListTable";
import PrintButtons from "./PrintButtons";
import ReactToPrint from "react-to-print";
import { useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  doc,
  getDoc,
  getDocs,
  updateDoc,
  onSnapshot,
  query,
  collection,
  where,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebase";

import { MASS_UNITS } from "../../constants";
import {
  buildProductionSchedule,
  deriveQueueFromProductionData,
  formatMinutesToHoursMinutes,
  getOvenEffectiveRate,
  normalizeOvenProducts,
} from "../../utils/productionScheduling";

import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

const MAX_SAFE_INPUT = 1_000_000; // guardrail to prevent UI freezes / Infinity / huge loops

const clampInt = (value, min = 0, max = MAX_SAFE_INPUT) => {
  if (value === "" || value === null || value === undefined) return 0;
  const n = typeof value === "number" ? value : Number(String(value).trim());
  if (!Number.isFinite(n)) return 0;
  const i = Math.trunc(n);
  return Math.min(max, Math.max(min, i));
};

const safeNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const Production = () => {
  const { type, objectId } = useParams();

  const [ingredients, setIngredients] = useState([]);
  const [doughs, setDoughs] = useState([]);
  const [products, setProducts] = useState([]);
  const [lines, setLines] = useState([]);
  const [productionLists, setProductionLists] = useState([]);
  const [ovens, setOvens] = useState([]);

  const [productionDate, setProductionDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [documentData, setDocumentData] = useState({
    selectedProductionLists: [],
    lineProductQuantities: {},
    startTimes: {},
    breakTimes: {},
    productionQueue: [],
    productionStartTime: "06:00",
  });

  const [documentId, setDocumentId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ------------ COLLAPSE STATE FOR PRODUCT ROWS -------------
  const [collapsedRows, setCollapsedRows] = useState(() => {
    const raw = localStorage.getItem("productionCollapsedRows");
    try {
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem(
      "productionCollapsedRows",
      JSON.stringify(collapsedRows)
    );
  }, [collapsedRows]);

  const toggleRowCollapse = (listId, lineId, productId) => {
    const key = `${listId}_${lineId}_${productId}`;
    setCollapsedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };
  const isRowCollapsed = (listId, lineId, productId) => {
    return collapsedRows[`${listId}_${lineId}_${productId}`];
  };

  // ----------------------------------------------------------

  const organizationId = JSON.parse(localStorage.getItem("organizations"))
    .selected.id;

  const componentRef = useRef();
  const labelsComponentRef = useRef();
  const ovenTimingComponentRef = useRef();

  const getDefaultUnitForProduct = (product) => {
    if (!product) return { name: "Piece", pieceCount: 1 };
    if (product.productUnits && product.productUnits.length > 0) {
      return {
        name: product.productUnits[0].name,
        pieceCount: product.productUnits[0].pieceCount,
      };
    }
    return { name: "Piece", pieceCount: 1 };
  };

  const fetchItems = (collectionName, stateUpdater) => {
    return onSnapshot(
      collection(db, "organizations", organizationId, collectionName),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: collectionName + "/" + doc.id,
          collection: collectionName,
          ...doc.data(),
        }));
        stateUpdater(data);
      },
      (error) => {
        console.log(error);
      }
    );
  };

  const fetchProduction = () => {
    return onSnapshot(
      query(
        collection(db, "organizations", organizationId, "productions"),
        where("date", "==", productionDate)
      ),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          collection: "productions",
          ...doc.data(),
        }));
        if (data.length > 0) {
          setDocumentData((prev) => ({
            ...prev,
            ...data[0],
            productionQueue: data[0].productionQueue || [],
            productionStartTime: data[0].productionStartTime || "06:00",
          }));
          setDocumentId(data[0].id);
        } else {
          setDocumentData((prev) => ({
            ...prev,
            selectedProductionLists: prev.selectedProductionLists || [],
            productionQueue: prev.productionQueue || [],
            productionStartTime: prev.productionStartTime || "06:00",
          }));
          setDocumentId(null);
        }
      },
      (error) => {
        console.log(error);
      }
    );
  };

  useEffect(() => {
    const unsubscribeIngredients = fetchItems("ingredients", setIngredients);
    const unsubscribeDoughs = fetchItems("doughs", setDoughs);
    const unsubscribeProducts = fetchItems("products", setProducts);
    const unsubscribeLines = fetchItems("lines", setLines);
    const unsubscribeProductionLists = fetchItems(
      "productionlists",
      setProductionLists
    );
    const unsubscribeOvens = fetchItems("ovens", setOvens);

    const unsubscribeProduction = fetchProduction();

    return () => {
      unsubscribeIngredients();
      unsubscribeDoughs();
      unsubscribeProducts();
      unsubscribeLines();
      unsubscribeProductionLists();
      unsubscribeProduction();
      unsubscribeOvens();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, objectId, organizationId, productionDate]);

  const getDayOfWeek = (dateString) => {
    const date = new Date(dateString);
    const daysOfWeek = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return daysOfWeek[date.getDay()];
  };

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    const formattedDay = String(date.getDate()).padStart(2, "0");
    const formattedMonth = String(date.getMonth() + 1).padStart(2, "0");
    const formattedYear = String(date.getFullYear());
    return `${formattedDay}-${formattedMonth}-${formattedYear}`;
  };

  const formatTimeToAMPM = (time) => {
    if (!time) return "";
    const [hour, minute] = time.split(":").map(Number);
    if (isNaN(hour) || isNaN(minute)) return time;
    const ampm = hour >= 12 ? "PM" : "AM";
    const adjustedHour = ((hour + 11) % 12) + 1;
    return `${adjustedHour}:${minute.toString().padStart(2, "0")} ${ampm}`;
  };

  const allItems = useMemo(
    () => [...ingredients, ...doughs, ...products],
    [ingredients, doughs, products]
  );

  const findLineByRef = (ref) => {
    if (!ref) return null;
    const s = String(ref);
    const plain = s.split("/").pop();
    return (
      lines.find(
        (l) =>
          String(l.id) === s ||
          String(l.id) === `lines/${plain}` ||
          String(l.id).split("/").pop() === plain
      ) || null
    );
  };

  const findItemByRef = (ref) => {
    if (!ref) return null;
    const s = String(ref);
    const plain = s.split("/").pop();
    return (
      allItems.find(
        (i) =>
          String(i.id) === s ||
          String(i.id) === `products/${plain}` ||
          String(i.id) === `ingredients/${plain}` ||
          String(i.id).split("/").pop() === plain
      ) || null
    );
  };

  const getLineByProductRef = (lineRef) => {
    const line = findLineByRef(lineRef);
    return line ? line : { name: "Line not found", products: [] };
  };

  const getItemByProductRef = (productRef) => {
    const item = findItemByRef(productRef);
    return item
      ? item
      : {
          name: "Product not found",
          ingredients: {},
          productUnits: [],
          maxMix: 0,
        };
  };

  const isFlattenedFormat = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return false;
    const first = arr[0];
    return (
      first &&
      (Object.prototype.hasOwnProperty.call(first, "lineId") ||
        Object.prototype.hasOwnProperty.call(first, "productId"))
    );
  };

  const normalizeToFlattened = (raw) => {
    if (!raw) return [];
    if (isFlattenedFormat(raw)) {
      return raw
        .map((it) => {
          if (!it) return null;
          if (it.lineId && it.productId)
            return { lineId: it.lineId, productId: it.productId };
          const lineId = it.lineId || it.line || it.lineRef;
          const productId = it.productId || it.product || it.productRef;
          if (lineId && productId) return { lineId, productId };
          return null;
        })
        .filter(Boolean);
    }

    const flattened = [];
    if (Array.isArray(raw)) {
      raw.forEach((entry) => {
        if (!entry || typeof entry !== "object") return;
        const keys = Object.keys(entry);
        keys.forEach((lineKey) => {
          const list = entry[lineKey] || [];
          if (Array.isArray(list)) {
            list.forEach((prod) => {
              if (prod) {
                const normalizedLineRef = lineKey.startsWith("lines/")
                  ? lineKey
                  : `lines/${lineKey}`;
                flattened.push({ lineId: normalizedLineRef, productId: prod });
              }
            });
          }
        });
      });
    }
    return flattened;
  };

  const groupFlattenedToGrouped = (flattened) => {
    const groupedMap = {};
    const order = [];
    (flattened || []).forEach((pair) => {
      if (!pair || !pair.lineId || !pair.productId) return;
      const lid = String(pair.lineId).startsWith("lines/")
        ? pair.lineId
        : `lines/${String(pair.lineId).split("/").pop()}`;
      const pid = String(pair.productId).startsWith("products/")
        ? pair.productId
        : `products/${String(pair.productId).split("/").pop()}`;
      if (!groupedMap[lid]) {
        groupedMap[lid] = [];
        order.push(lid);
      }
      groupedMap[lid].push(pid);
    });
    return order.map((lid) => ({ [lid]: groupedMap[lid] || [] }));
  };

  const enrichedProductionLists = useMemo(() => {
    return productionLists.map((pl) => {
      const raw = pl.linesAndProducts ?? pl.lines ?? pl.products ?? [];
      const flattenedRaw = normalizeToFlattened(raw) || [];
      const flattened = flattenedRaw.map((p) => {
        const lineRef = String(p.lineId).startsWith("lines/")
          ? p.lineId
          : `lines/${String(p.lineId).split("/").pop()}`;
        const prodRef = String(p.productId).startsWith("products/")
          ? p.productId
          : `products/${String(p.productId).split("/").pop()}`;
        return { lineId: lineRef, productId: prodRef };
      });

      const grouped = groupFlattenedToGrouped(flattened);
      const groupedMap = {};
      grouped.forEach((entry) => {
        const k = Object.keys(entry)[0];
        groupedMap[k] = Array.isArray(entry[k]) ? entry[k] : [];
      });

      const table = flattened.map((p, idx) => {
        const lineObj = findLineByRef(p.lineId);
        const itemObj = findItemByRef(p.productId);
        return {
          seq: idx + 1,
          lineId: p.lineId,
          lineName: lineObj ? lineObj.name : "",
          productId: p.productId,
          productName: itemObj ? itemObj.name : "",
        };
      });

      return {
        ...pl,
        linesAndProducts: grouped,
        linesAndProductsGrouped: grouped,
        linesAndProductsFlattened: flattened,
        linesAndProductsMap: groupedMap,
        linesAndProductsTable: table,
      };
    });
  }, [productionLists, allItems, lines]);

  const getCurrentTimeHHMM = () => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  useEffect(() => {
    const lpq = documentData.lineProductQuantities || {};
    let changed = false;
    const newStartTimes = { ...(documentData.startTimes || {}) };

    Object.entries(lpq).forEach(([listId, linesObj]) => {
      if (!newStartTimes[listId]) {
        newStartTimes[listId] = {};
        changed = true;
      }
      Object.keys(linesObj || {}).forEach((lineId) => {
        if (!newStartTimes[listId]?.[lineId]) {
          newStartTimes[listId] = {
            ...(newStartTimes[listId] || {}),
            [lineId]: getCurrentTimeHHMM(),
          };
          changed = true;
        }
      });
    });

    if (changed) {
      setDocumentData((prev) => ({ ...prev, startTimes: newStartTimes }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentData.lineProductQuantities]);

  const ensureProductHasMeta = (listId, lineId, productId, updatedData) => {
    if (!updatedData.lineProductQuantities[listId]) {
      updatedData.lineProductQuantities[listId] = {};
    }
    if (!updatedData.lineProductQuantities[listId][lineId]) {
      updatedData.lineProductQuantities[listId][lineId] = {};
    }
    if (!updatedData.lineProductQuantities[listId][lineId][productId]) {
      updatedData.lineProductQuantities[listId][lineId][productId] = {};
    }
    if (!updatedData.lineProductQuantities[listId][lineId][productId].unit) {
      const product = getItemByProductRef(productId);
      updatedData.lineProductQuantities[listId][lineId][productId].unit =
        getDefaultUnitForProduct(product);
      updatedData.lineProductQuantities[listId][lineId][productId].sequence =
        Object.keys(updatedData.lineProductQuantities[listId][lineId]).length;
    }
  };

  // NOTE: crash prevention: clamp quantities and guard against non-finite numbers.
  const handleQuantityChange = (listId, lineId, productId, quantity) => {
    const next = clampInt(quantity, 0, MAX_SAFE_INPUT);

    setDocumentData((prev) => {
      const updatedData = { ...prev };
      ensureProductHasMeta(listId, lineId, productId, updatedData);
      updatedData.lineProductQuantities[listId][lineId][productId].quantity =
        next;
      return updatedData;
    });

    if (String(quantity).trim() !== "" && next === MAX_SAFE_INPUT) {
      setSnackbar({
        open: true,
        message: `Quantity capped at ${MAX_SAFE_INPUT.toLocaleString()} to prevent performance issues.`,
        severity: "warning",
      });
    }
  };

  const handleOnHandChange = (listId, lineId, productId, quantity) => {
    const next = clampInt(quantity, 0, MAX_SAFE_INPUT);

    setDocumentData((prev) => {
      const updatedData = { ...prev };
      ensureProductHasMeta(listId, lineId, productId, updatedData);
      updatedData.lineProductQuantities[listId][lineId][productId].onHand =
        next;
      return updatedData;
    });
  };

  const handleExtraChange = (listId, lineId, productId, quantity) => {
    const next = clampInt(quantity, 0, MAX_SAFE_INPUT);

    setDocumentData((prev) => {
      const updatedData = { ...prev };
      ensureProductHasMeta(listId, lineId, productId, updatedData);
      updatedData.lineProductQuantities[listId][lineId][productId].extra =
        next;
      return updatedData;
    });
  };

  const handleReworkChange = (listId, lineId, productId, checked) => {
    setDocumentData((prev) => {
      const updatedData = { ...prev };
      ensureProductHasMeta(listId, lineId, productId, updatedData);
      updatedData.lineProductQuantities[listId][lineId][productId].rework =
        !!checked;
      return updatedData;
    });
  };

  const handleUnitChange = (listId, lineId, productId, event) => {
    const pieceCount = safeNumber(event.value, 1) || 1;
    const name = event.getAttribute("name") || "Piece";

    setDocumentData((prev) => {
      const updatedData = { ...prev };
      ensureProductHasMeta(listId, lineId, productId, updatedData);
      updatedData.lineProductQuantities[listId][lineId][productId].unit = {
        pieceCount,
        name,
      };
      return updatedData;
    });
  };

  const removeProductionList = (listId) => {
    setDocumentData((prev) => ({
      ...prev,
      selectedProductionLists: prev.selectedProductionLists.filter(
        (id) => id !== listId
      ),
    }));
    // Clean collapsed state for removed list
    setCollapsedRows((prev) => {
      const newCollapsed = { ...prev };
      Object.keys(newCollapsed).forEach((key) => {
        if (key.startsWith(`${listId}_`)) {
          delete newCollapsed[key];
        }
      });
      return newCollapsed;
    });
  };

  const moveProductionList = (index, direction) => {
    setDocumentData((prev) => {
      const newList = [...prev.selectedProductionLists];
      const [movedItem] = newList.splice(index, 1);
      newList.splice(index + direction, 0, movedItem);
      return {
        ...prev,
        selectedProductionLists: newList,
      };
    });
  };

  const moveProductUp = (listId, lineId, productId) => {
    setDocumentData((prev) => {
      const lineProductQuantities = prev.lineProductQuantities || {};
      const products = lineProductQuantities[listId]?.[lineId] || {};
      const productEntries = Object.entries(products);
      const sortedProductEntries = productEntries.sort(
        ([, a], [, b]) => (a.sequence ?? 0) - (b.sequence ?? 0)
      );
      const index = sortedProductEntries.findIndex(([id]) => id === productId);
      if (index <= 0) return prev;
      const newProductEntries = [...sortedProductEntries];
      [newProductEntries[index - 1], newProductEntries[index]] = [
        newProductEntries[index],
        newProductEntries[index - 1],
      ];
      const updatedProductEntries = newProductProductEntriesToSequenced(
        newProductEntries
      );
      return {
        ...prev,
        lineProductQuantities: {
          ...lineProductQuantities,
          [listId]: {
            ...(lineProductQuantities[listId] || {}),
            [lineId]: Object.fromEntries(updatedProductEntries),
          },
        },
      };
    });
  };

  const moveProductDown = (listId, lineId, productId) => {
    setDocumentData((prev) => {
      const lineProductQuantities = prev.lineProductQuantities || {};
      const products = lineProductQuantities[listId]?.[lineId] || {};
      const productEntries = Object.entries(products);
      const sortedProductEntries = productEntries.sort(
        ([, a], [, b]) => (a.sequence ?? 0) - (b.sequence ?? 0)
      );
      const index = sortedProductEntries.findIndex(([id]) => id === productId);
      if (index < 0 || index >= sortedProductEntries.length - 1) return prev;
      const newProductEntries = [...sortedProductEntries];
      [newProductEntries[index + 1], newProductEntries[index]] = [
        newProductEntries[index],
        newProductEntries[index + 1],
      ];
      const updatedProductEntries = newProductProductEntriesToSequenced(
        newProductEntries
      );
      return {
        ...prev,
        lineProductQuantities: {
          ...lineProductQuantities,
          [listId]: {
            ...(lineProductQuantities[listId] || {}),
            [lineId]: Object.fromEntries(updatedProductEntries),
          },
        },
      };
    });
  };

  const newProductProductEntriesToSequenced = (entries) =>
    entries.map(([id, data], i) => [id, { ...data, sequence: i + 1 }]);

  const handleBreakTimeChange = (
    selectedListId,
    lineId,
    productId,
    mixIndex,
    value
  ) => {
    setDocumentData((prev) => {
      const updated = { ...prev };
      if (!updated.breakTimes) updated.breakTimes = {};
      if (!updated.breakTimes[selectedListId])
        updated.breakTimes[selectedListId] = {};
      if (!updated.breakTimes[selectedListId][lineId])
        updated.breakTimes[selectedListId][lineId] = {};
      if (!updated.breakTimes[selectedListId][lineId][productId])
        updated.breakTimes[selectedListId][lineId][productId] = {};
      updated.breakTimes[selectedListId][lineId][productId][mixIndex] =
        clampInt(value, 0, 24 * 60);
      return updated;
    });
  };

  const saveProductionData = async () => {
    setSaving(true);
    try {
      const filteredData = {
        ...documentData,
        date: productionDate,
      };

      if (filteredData.lineProductQuantities) {
        Object.entries(filteredData.lineProductQuantities).forEach(
          ([listId, linesObj]) => {
            Object.entries(linesObj).forEach(([lineId, products]) => {
              Object.entries(products).forEach(([productId, data]) => {
                const pieceCount = safeNumber(data.unit?.pieceCount, 1) || 1;
                const quantityNeeded = Math.max(
                  0,
                  clampInt(data.quantity) -
                    clampInt(data.onHand) +
                    clampInt(data.extra)
                );
                const totalPieces = quantityNeeded * pieceCount;

                if (totalPieces <= 0) {
                  delete filteredData.lineProductQuantities[listId][lineId][
                    productId
                  ];
                }
              });

              if (
                Object.keys(filteredData.lineProductQuantities[listId][lineId])
                  .length === 0
              ) {
                delete filteredData.lineProductQuantities[listId][lineId];
              }
            });

            if (
              Object.keys(filteredData.lineProductQuantities[listId]).length === 0
            ) {
              delete filteredData.lineProductQuantities[listId];
            }
          }
        );
      }

      filteredData.selectedProductionLists = (
        filteredData.selectedProductionLists || []
      ).filter(
        (listId) =>
          filteredData.lineProductQuantities &&
          filteredData.lineProductQuantities[listId] !== undefined
      );

      if (documentId) {
        const docRef = doc(db, "organizations", organizationId, "productions", documentId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          await updateDoc(docRef, filteredData);
          setSnackbar({
            open: true,
            message: "Successfully saved.",
            severity: "success",
          });
          setDocumentId(documentId);
          setDocumentData(filteredData);
          setSaving(false);
          return;
        }
      }

      const existingQuery = query(
        collection(db, "organizations", organizationId, "productions"),
        where("date", "==", productionDate)
      );
      const existingSnap = await getDocs(existingQuery);

      if (!existingSnap.empty) {
        const existingDoc = existingSnap.docs[0];
        const existingDocRef = doc(db, "organizations", organizationId, "productions", existingDoc.id);
        await updateDoc(existingDocRef, filteredData);
        setDocumentId(existingDoc.id);
        setSnackbar({
          open: true,
          message: "Successfully saved (updated existing production).",
          severity: "success",
        });
        setDocumentData(filteredData);
        setSaving(false);
        return;
      }

      const newDocRef = doc(collection(db, "organizations", organizationId, "productions"));
      await setDoc(newDocRef, filteredData);
      setDocumentId(newDocRef.id);
      setSnackbar({
        open: true,
        message: "Successfully saved (new document created).",
        severity: "success",
      });
      setDocumentData(filteredData);
    } catch (error) {
      console.error("Error saving document: ", error);
      setSnackbar({
        open: true,
        message: `Error saving: ${error.message || error}`,
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleStartTimeChange = (listId, lineId, value) => {
    setDocumentData((prevData) => ({
      ...prevData,
      startTimes: {
        ...prevData.startTimes,
        [listId]: {
          ...prevData.startTimes?.[listId],
          [lineId]: value,
        },
      },
    }));
  };

  const addQueueBatch = () => {
    setDocumentData((prev) => ({
      ...prev,
      productionQueue: [
        ...(prev.productionQueue || []),
        {
          id: `batch-${Date.now()}`,
          productRef: "",
          quantity: 0,
          priority: (prev.productionQueue || []).length + 1,
          manualOvenId: "",
        },
      ],
    }));
  };

  const removeQueueBatch = (batchId) => {
    setDocumentData((prev) => ({
      ...prev,
      productionQueue: (prev.productionQueue || []).filter((batch) => batch.id !== batchId),
    }));
  };

  const updateQueueBatch = (batchId, key, value) => {
    setDocumentData((prev) => ({
      ...prev,
      productionQueue: (prev.productionQueue || []).map((batch) =>
        batch.id === batchId
          ? {
              ...batch,
              [key]:
                key === "quantity" || key === "priority"
                  ? clampInt(value, 0, MAX_SAFE_INPUT)
                  : value,
            }
          : batch
      ),
    }));
  };

  const clearManualOvenAssignments = () => {
    setDocumentData((prev) => ({
      ...prev,
      productionQueue: (prev.productionQueue || []).map((batch) => ({
        ...batch,
        manualOvenId: "",
      })),
    }));
  };

  // refactor: avoid querying the whole document for inputs/selects; scope to the nearest form/table instead
  const handleEnterKey = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();

    const root =
      event.currentTarget?.closest?.("table") ||
      event.currentTarget?.closest?.("form") ||
      document;

    const formElements = Array.from(
      root.querySelectorAll("input, select, textarea, button")
    ).filter((el) => !el.disabled && el.tabIndex !== -1);

    const currentIndex = formElements.indexOf(event.target);
    if (currentIndex >= 0 && currentIndex < formElements.length - 1) {
      formElements[currentIndex + 1].focus();
    }
  };

  const calculateTotal = (ingredient, data, lineId, productId) => {
    const pieceCount = safeNumber(data.unit?.pieceCount, 1) || 1;
    const quantityNeeded = Math.max(
      0,
      clampInt(data.quantity) - clampInt(data.onHand) + clampInt(data.extra)
    );
    const ingredientAmount = safeNumber(Object.values(ingredient)[0], 0);

    const product = getLineByProductRef(lineId).products.find(
      (item) => Object.keys(item)[0] === productId
    )?.[productId];
    const scrapPercent = safeNumber(product?.scrapPercent, 0);

    const totalInG =
      Math.round(
        ingredientAmount *
          quantityNeeded *
          pieceCount *
          (1 + scrapPercent / 100) *
          100
      ) / 100;

    return Number.isFinite(totalInG) ? totalInG : 0;
  };

  const calculateScale = (ingredient, data) => {
    const pieceCount = safeNumber(data.unit?.pieceCount, 1) || 1;
    const ingredientAmount = safeNumber(Object.entries(ingredient)[0][1], 0);
    const needed = Math.max(
      0,
      clampInt(data.quantity) - clampInt(data.onHand) + clampInt(data.extra)
    );

    const denom = safeNumber(MASS_UNITS?.[0]?.amount, 1) || 1;

    const scale = Math.round(((ingredientAmount * needed * pieceCount) / denom) * 100) / 100;
    return `Scale ${Number.isFinite(scale) ? scale : 0} ${MASS_UNITS[0].abbreviation} `;
  };

  const calculateScrap = (ingredient, data, lineId, productId) => {
    const pieceCount = safeNumber(data.unit?.pieceCount, 1) || 1;
    const product = getLineByProductRef(lineId).products.find(
      (item) => Object.keys(item)[0] === productId
    )?.[productId];
    const scrapPercent = safeNumber(product?.scrapPercent, 0);
    const ingredientAmount = safeNumber(Object.entries(ingredient)[0][1], 0);

    const denom = safeNumber(MASS_UNITS?.[0]?.amount, 1) || 1;
    const needed = Math.max(
      0,
      clampInt(data.quantity) - clampInt(data.onHand) + clampInt(data.extra)
    );

    const baseAmount = (ingredientAmount * needed * pieceCount) / denom;
    const scrapMass = Math.round(baseAmount * (scrapPercent / 100) * 100) / 100;
    return `Scrap ${Number.isFinite(scrapMass) ? scrapMass : 0} (${scrapPercent})%`;
  };

  const calculateMixes = (ingredient, data, lineId, productId) => {
    const line = getLineByProductRef(lineId);
    if (!line || !line.products) {
      console.error("Line or its products are undefined");
      return { mixes: "N/A", reworkMixes: "N/A" };
    }

    const productItem = line.products.find(
      (item) => Object.keys(item)[0] === productId
    );
    const scrapPercent = safeNumber(productItem ? productItem[productId]?.scrapPercent : 0, 0);

    const ingredientId = Object.keys(ingredient)[0];
    const ingredientAmount = safeNumber(Object.values(ingredient)[0], 0);

    const quantityNeeded = Math.max(
      0,
      clampInt(data.quantity) - clampInt(data.onHand) + clampInt(data.extra)
    );
    const baseAmount =
      ingredientAmount * quantityNeeded * (safeNumber(data.unit?.pieceCount, 1) || 1);

    let mixes = null;
    let reworkMixes = null;

    if (ingredientId?.startsWith("doughs/")) {
      const maxMix = safeNumber(getItemByProductRef(ingredientId).maxMix, 0);

      // crash fix: if maxMix is 0 or not finite, avoid division by zero -> Infinity (which later causes huge loops)
      if (!Number.isFinite(maxMix) || maxMix <= 0) {
        return { mixes: 0, reworkMixes: 0 };
      }

      const totalWithScrap = baseAmount * (1 + scrapPercent / 100);
      const scrapMass = baseAmount * (scrapPercent / 100);

      mixes = Math.round((totalWithScrap / maxMix) * 100) / 100;
      reworkMixes = Math.round((scrapMass / maxMix) * 100) / 100;

      // guard: prevent crazy huge loop counts; we’ll cap schedule generation later via loop limit
      mixes = Number.isFinite(mixes) ? mixes : 0;
      reworkMixes = Number.isFinite(reworkMixes) ? reworkMixes : 0;
    }

    return { mixes, reworkMixes };
  };

  const addMinutes = (time, minutes) => {
    const [hours, mins] = String(time || "00:00")
      .split(":")
      .map(Number);
    const date = new Date();
    date.setHours(Number.isFinite(hours) ? hours : 0);
    date.setMinutes((Number.isFinite(mins) ? mins : 0) + safeNumber(minutes, 0));
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const getCompatibleOvens = (productId) => {
    const target = String(productId || "");
    const plain = target.split("/").pop();
    return ovens.filter((oven) => {
      const matches = normalizeOvenProducts(oven.products).some((entry) => {
        const ref = String(entry.productRef || "");
        return ref === target || ref.split("/").pop() === plain;
      });
      return matches && getOvenEffectiveRate(oven, productId) > 0;
    });
  };

  const getBoardCount = (productId, pieces) => {
    const piecePerBoard = safeNumber(getItemByProductRef(productId).piecePerBoard, 0);
    return piecePerBoard ? safeNumber(pieces, 0) / piecePerBoard : 0;
  };

  const getRackCount = (productId, boards) => {
    const boardPerRack = safeNumber(getItemByProductRef(productId).boardPerRack, 0);
    return boardPerRack ? safeNumber(boards, 0) / boardPerRack : 0;
  };

  // derived data: compute totals locally rather than mutating outer vars during render
  const summaryResult = useMemo(() => {
    const ingredientTotals = {};
    let totalBoards = 0;
    let totalRacks = 0;

    const summaryData = Object.entries(documentData.lineProductQuantities || {}).map(
      ([listId, linesObj]) => {
        const listName =
          productionLists.find((list) => list.id === listId)?.name || "Not Found";

        const lineDetails = Object.entries(linesObj || {}).map(([lineId, products]) => {
          let startTime = documentData.startTimes?.[listId]?.[lineId] || "00:00";
          const lineName = getLineByProductRef(lineId).name;

          let productEntries = Object.entries(products || {}).map(
            ([productId, data]) => {
              const sequence = data.sequence ?? 9999;
              const pieceCount = safeNumber(data.unit?.pieceCount, 1) || 1;
              const quantityNeeded = Math.max(
                0,
                clampInt(data.quantity) - clampInt(data.onHand) + clampInt(data.extra)
              );
              const totalPieces = quantityNeeded * pieceCount;
              return { productId, data, sequence, totalPieces };
            }
          );

          productEntries = productEntries.filter((p) => p.totalPieces > 0);
          productEntries.sort((a, b) => a.sequence - b.sequence);

          const productDetails = productEntries.map(({ productId, data, sequence, totalPieces }) => {
            const productName = getItemByProductRef(productId).name;

            const boardsNeeded = Math.ceil(getBoardCount(productId, totalPieces));
            const racksNeeded = Math.ceil(getRackCount(productId, boardsNeeded));
            totalBoards += boardsNeeded;
            totalRacks += racksNeeded;

            const ingredientDetails = Object.entries(
              getItemByProductRef(productId).ingredients || {}
            ).map(([, ingredient]) => {
              const ingredientId = Object.keys(ingredient)[0];
              const ingredientName = getItemByProductRef(ingredientId).name;

              const { mixes, reworkMixes } = ingredientId.startsWith("doughs/")
                ? calculateMixes(ingredient, data, lineId, productId)
                : { mixes: null, reworkMixes: null };

              const dough = getItemByProductRef(ingredientId);
              const mixMax = safeNumber(dough.maxMix, 0);
              const mixMin = safeNumber(dough.minMix, 0);

              const total = calculateTotal(ingredient, data, lineId, productId);
              const scale = calculateScale(ingredient, data);
              const scrap = calculateScrap(ingredient, data, lineId, productId);

              const scrapPercent =
                safeNumber(
                  getLineByProductRef(lineId).products.find(
                    (item) => Object.keys(item)[0] === productId
                  )?.[productId]?.scrapPercent,
                  0
                );

              const manufacturerCode = dough.manufacturerCode;

              // ingredient totals (for doughs that have recipes)
              const doughRecipe = dough.ingredients || [];
              const doughTotalWeight = doughRecipe.reduce((sum, obj) => {
                const amount = safeNumber(Object.values(obj)[0], 0);
                return sum + amount;
              }, 0);

              doughRecipe.forEach((ingredientObj) => {
                const ingredientId = Object.keys(ingredientObj)[0];
                const amount = safeNumber(ingredientObj[ingredientId], 0);
                const ratio = doughTotalWeight ? amount / doughTotalWeight : 0;
                const weightUsed = ratio * total;

                if (
                  !ingredientTotals[ingredientId] &&
                  getItemByProductRef(ingredientId).reportInProd
                ) {
                  ingredientTotals[ingredientId] = 0;
                }
                if (getItemByProductRef(ingredientId).reportInProd) {
                  ingredientTotals[ingredientId] += weightUsed;
                }
              });

              // mixer schedule generation: critical crash fix
              // - mixes can be fractional; we should only iterate integer count
              // - cap at a reasonable limit to avoid freezing if user inputs massive quantities
              const mixTimes = [];
              const mixesInt = Math.min(500, Math.max(0, Math.ceil(safeNumber(mixes, 0))));
              const reworkMixesInt = Math.min(500, Math.max(0, Math.ceil(safeNumber(reworkMixes, 0))));

              if (mixesInt > 0) {
                const mixDuration = safeNumber(dough.mixTime, 0);
                const mixDuration2 = safeNumber(dough.mixTime2, 0);
                const reposeTime = safeNumber(dough.fermentTime, 0);

                const reworkMixDuration = safeNumber(dough.reworkMixTime, 0);
                const reworkMixDuration2 = safeNumber(dough.reworkMixTime2, 0);
                const reworkFermentTime = safeNumber(dough.reworkFermentTime, 0);

                const piecesPerHour =
                  safeNumber(
                    getLineByProductRef(lineId).products.find((item) => item[productId])
                      ?.[productId]?.piecesPerHour,
                    1
                  ) || 1;

                const ingredientAmount = safeNumber(Object.values(ingredient)[0], 1) || 1;
                const piecesPerMix = ingredientAmount ? (mixMax || 1) / ingredientAmount : 0;

                for (let i = 0; i < mixesInt; i++) {
                  const startReposeTime = addMinutes(startTime, -reposeTime);
                  const startMixTime = addMinutes(startReposeTime, -mixDuration - mixDuration2);
                  const runTimeMins = Math.ceil((piecesPerMix / piecesPerHour) * 60);

                  mixTimes.push({
                    type: "normal",
                    startTime,
                    startMixTime,
                    startReposeTime,
                    runTime: startTime,
                  });

                  startTime = addMinutes(
                    startTime,
                    Math.ceil(runTimeMins * (1 - scrapPercent / 100))
                  );

                  const productBreakTime =
                    documentData.breakTimes?.[listId]?.[lineId]?.[productId]?.[i];
                  if (productBreakTime) {
                    startTime = addMinutes(startTime, clampInt(productBreakTime, 0, 24 * 60));
                  }
                }

                if (data.rework) {
                  for (let i = 0; i < reworkMixesInt; i++) {
                    const startReposeTime = addMinutes(startTime, -reworkFermentTime);
                    const startMixTime = addMinutes(
                      startReposeTime,
                      -reworkMixDuration - reworkMixDuration2
                    );
                    const runTimeMins = Math.ceil((piecesPerMix / piecesPerHour) * 60);

                    mixTimes.push({
                      type: "rework",
                      startTime,
                      startMixTime,
                      startReposeTime,
                      runTime: startTime,
                    });

                    startTime = addMinutes(
                      startTime,
                      Math.ceil(runTimeMins * (1 - scrapPercent / 100))
                    );

                    const productBreakTime =
                      documentData.breakTimes?.[listId]?.[lineId]?.[productId]?.[
                        mixTimes.length - 1
                      ];
                    if (productBreakTime) {
                      startTime = addMinutes(startTime, clampInt(productBreakTime, 0, 24 * 60));
                    }
                  }
                }
              }

              return {
                ingredientName,
                total,
                scale,
                scrap,
                scrapPercent,
                mixes,
                mixMax,
                mixMin,
                mixTimes,
                manufacturerCode,
              };
            });

            const compatibleOvens = getCompatibleOvens(productId);
            const assignedOven = compatibleOvens?.[0] ?? null;
            const ovenStartTime = startTime;
            let ovenEndTime = null;

            if (assignedOven) {
              const piecesPerHour = getOvenEffectiveRate(assignedOven, productId) || 1;
              const ovenDurationMins = Math.ceil((totalPieces / piecesPerHour) * 60);
              ovenEndTime = addMinutes(ovenStartTime, ovenDurationMins);
            }

            const ovenTimes = assignedOven
              ? {
                  ovenId: assignedOven.id,
                  ovenName: assignedOven.name,
                  startTime: ovenStartTime,
                  endTime: ovenEndTime,
                }
              : null;

            return {
              sequence,
              productName,
              productId,
              rework: data.rework ?? false,
              quantity: Math.max(
                0,
                clampInt(data.quantity) - clampInt(data.onHand) + clampInt(data.extra)
              ),
              onHand: clampInt(data.onHand),
              filledQuantity: clampInt(data.quantity),
              extra: clampInt(data.extra),
              unitName: data.unit?.name ?? "Piece",
              pieceCount: safeNumber(data.unit?.pieceCount, 1) || 1,
              totalPieces,
              racksNeeded,
              boardsNeeded,
              ingredientDetails,
              ovenTimes,
            };
          });

          return { lineName, lineId, products: productDetails };
        });

        return { listName, listId, lines: lineDetails };
      }
    );

    return { summaryData, ingredientTotals, totalBoards, totalRacks };
  }, [documentData, productionLists, ovens, lines, allItems]);

  const { summaryData, ingredientTotals, totalBoards, totalRacks } = summaryResult;

  const productionSchedule = useMemo(
    () =>
      buildProductionSchedule({
        queue: documentData.productionQueue || [],
        ovens,
        startTime: documentData.productionStartTime || "06:00",
      }),
    [documentData.productionQueue, documentData.productionStartTime, ovens]
  );

  // Derive the baking timing queue automatically from production list quantities.
  // This computes which products have non-zero piece totals and generates stable
  // queue entries so the Baking Timing table is always in sync with what was entered.
  const derivedQueue = useMemo(
    () =>
      deriveQueueFromProductionData({
        selectedProductionLists: documentData.selectedProductionLists,
        lineProductQuantities: documentData.lineProductQuantities,
        productionDate,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentData.selectedProductionLists, documentData.lineProductQuantities, productionDate]
  );

  // Sync derived queue into documentData.productionQueue whenever quantities change.
  // Uses a ref to track the last synced value so we don't loop on every productionQueue update.
  const lastSyncedDerivedQueueRef = useRef(null);
  useEffect(() => {
    if (lastSyncedDerivedQueueRef.current === derivedQueue) return;
    lastSyncedDerivedQueueRef.current = derivedQueue;

    setDocumentData((prev) => {
      // Preserve manualOvenId from existing auto-generated entries so user overrides survive
      const overrides = {};
      (prev.productionQueue || []).forEach((batch) => {
        if (String(batch.id).startsWith("auto-") && batch.manualOvenId) {
          overrides[batch.id] = batch.manualOvenId;
        }
      });

      const mergedAutoQueue = derivedQueue.map((batch) => ({
        ...batch,
        manualOvenId: overrides[batch.id] || "",
      }));

      // Preserve any manually added batches (those with non-auto IDs)
      const manualBatches = (prev.productionQueue || []).filter(
        (b) => !String(b.id).startsWith("auto-")
      );

      return {
        ...prev,
        productionQueue: [...mergedAutoQueue, ...manualBatches],
      };
    });
  }, [derivedQueue]);

  const truncateMiddleBeforeParen = (text, maxLength = 28) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;

    const parenIndex = text.lastIndexOf("(");
    if (parenIndex === -1) {
      const halfLength = Math.floor((maxLength - 3) / 2);
      return (
        text.substring(0, halfLength) +
        "..." +
        text.substring(text.length - halfLength)
      );
    }

    const endPart = text.substring(parenIndex);
    const allowedMainLength = maxLength - endPart.length;

    if (allowedMainLength <= 3) {
      return "..." + endPart;
    }

    const mainPart = text.substring(0, parenIndex).trim();
    if (mainPart.length <= allowedMainLength) {
      return mainPart + endPart;
    }

    const halfLength = Math.floor((allowedMainLength - 3) / 2);
    return (
      mainPart.substring(0, halfLength) +
      "..." +
      mainPart.substring(mainPart.length - halfLength) +
      endPart
    );
  };

  const generateMixerChartTable = () => {
    return summaryData.map((list) => (
      <div key={list.listName} className="mixerChartListBlock">
        
        {list.lines.map((line, index) => {
          const sortedProducts = [...line.products].sort((a, b) => {
            const sequenceA =
              documentData?.lineProductQuantities?.[list.listId]?.[line.lineId]?.[
                a.productId
              ]?.sequence ?? 0;
            const sequenceB =
              documentData?.lineProductQuantities?.[list.listId]?.[line.lineId]?.[
                b.productId
              ]?.sequence ?? 0;
            return sequenceA - sequenceB;
          });

          return (
            <div key={line.lineName} className="mixerChartLineBlock">
              <strong>{list.listName}</strong>
              <strong>Mix Schedule Line: {line.lineName}</strong>
              <table className="mixerChartTable">
                <thead>
                  <tr>
                    <th>Dough</th>
                    <th>Product</th>
                    
                    <th>Mix</th>
                    <th>Repose</th>
                    <th>Start</th>
                    <th>Break</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedProducts.map((product) =>
                    product.ingredientDetails.map((ingredient) =>
                      ingredient.mixTimes.map((mix, mixIndex) => (
                        <tr key={`${product.productId}-${ingredient.ingredientName}-${mixIndex}`}>
                          <td>
                            {truncateMiddleBeforeParen(
                              `${ingredient.manufacturerCode} #${mixIndex + 1}${
                                mix.type === "rework" ? "RW" : ""
                              }`
                            )}
                          </td>                          
                          {/* <td>
                            {truncateMiddleBeforeParen(
                              `${ingredient.ingredientName} (${ingredient.manufacturerCode}) ${mixIndex + 1}${
                                mix.type === "rework" ? "RW" : ""
                              }`
                            )}
                          </td> */}
                          <td>{truncateMiddleBeforeParen(product.productName)}</td>
                          <td>{formatTimeToAMPM(mix.startMixTime)}</td>
                          <td>{formatTimeToAMPM(mix.startReposeTime)}</td>
                          <td>{formatTimeToAMPM(mix.startTime)}</td>
                          <td>
                            <input
                              type="number"
                              min={0}
                              max={24 * 60}
                              style={{
                                width: "64px",
                                padding: "0px 0px",
                                fontSize: "0.95em",
                              }}
                              value={
                                documentData.breakTimes?.[list.listId]?.[line.lineId]?.[
                                  product.productId
                                ]?.[mixIndex] ?? ""
                              }
                              onChange={(e) =>
                                handleBreakTimeChange(
                                  list.listId,
                                  line.lineId,
                                  product.productId,
                                  mixIndex,
                                  e.target.value
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))
                    )
                  )}
                </tbody>
              </table>
              {/* {index + 1 < list.lines.length ? (
                <div className="page-break"></div>
              ) : null} */}
            </div>
          );
        })}
      </div>
    ));
  };

  const formatBakingMinutesLabel = (minutes) => {
    const totalMinutes = clampInt(minutes);
    return `${totalMinutes} min (${formatMinutesToHoursMinutes(totalMinutes)})`;
  };

  return (
    <div className="single">
      <Sidebar />
      <div className="singleContainer">
        <Navbar />

        {/* UI polish: simple page header area */}
        <div style={{ padding: "16px 16px 0 16px" }}>
          <h2 style={{ margin: 0 }}>Production</h2>
          <div style={{ opacity: 0.75, marginTop: 4, fontSize: 14 }}>
            Plan quantities, generate mix schedule, and print labels for{" "}
            <strong>{getDayOfWeek(productionDate)}</strong> ({formatDate(productionDate)})
          </div>
        </div>

        <ProductionLineTable
          documentData={documentData}
          handleQuantityChange={handleQuantityChange}
          handleUnitChange={handleUnitChange}
          handleReworkChange={handleReworkChange}
          handleExtraChange={handleExtraChange}
          handleEnterKey={handleEnterKey}
          getItemByProductRef={getItemByProductRef}
          getLineByProductRef={getLineByProductRef}
          productionDate={productionDate}
          setProductionDate={setProductionDate}
          setDocumentData={setDocumentData}
          productionLists={enrichedProductionLists}
          handleStartTimeChange={handleStartTimeChange}
          handleOnHandChange={handleOnHandChange}
          moveProductionList={moveProductionList}
          removeProductionList={removeProductionList}
          isRowCollapsed={isRowCollapsed}
          toggleRowCollapse={toggleRowCollapse}
        />

        <div style={{ padding: "12px 16px", display: "flex", gap: 12, alignItems: "center" }}>
          <button onClick={saveProductionData} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
          <div style={{ fontSize: 13, opacity: 0.7 }}>
            Tip: very large quantities are capped to prevent freezes.
          </div>
        </div>

        <PrintButtons
          componentRef={componentRef}
          labelsComponentRef={labelsComponentRef}
          summaryData={summaryData}
          productionDate={productionDate}
          generateMixerChartTable={generateMixerChartTable}
          formatDate={formatDate}
          getDayOfWeek={getDayOfWeek}
          formatTimeToAMPM={formatTimeToAMPM}
          SummaryPrintableComponent={SummaryPrintableComponent}
          LabelsPrintableComponent={LabelsPrintableComponent}
        />

        {documentData.selectedProductionLists.length > 0 ? (
          <>
            <div style={{ padding: "0 16px" }}>
              <h3>Summary</h3>

              {Object.entries(documentData.lineProductQuantities || {}).map(
                ([listId, linesObj]) => (
                  <div key={listId} style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 600 }}>
                      Production List:{" "}
                      {productionLists.find((list) => list.id === listId)
                        ? productionLists.find((list) => list.id === listId).name
                        : "Not Found"}
                    </div>

                    <ul>
                      {Object.entries(linesObj || {}).map(([lineId, products]) => (
                        <li key={lineId}>
                          <span style={{ fontWeight: 600 }}>
                            Line: {getLineByProductRef(lineId).name}
                          </span>{" "}
                          <span style={{ opacity: 0.8 }}>
                            Start:{" "}
                            {documentData.startTimes?.[listId]?.[lineId] || "Not Set"}
                          </span>

                          <ul>
                            {Object.entries(products || {})
                              .sort(
                                ([, aData], [, bData]) =>
                                  (aData.sequence ?? 0) - (bData.sequence ?? 0)
                              )
                              .filter(([, data]) => {
                                const pieceCount = safeNumber(data.unit?.pieceCount, 1) || 1;
                                const needed =
                                  Math.max(
                                    0,
                                    clampInt(data.quantity) -
                                      clampInt(data.onHand) +
                                      clampInt(data.extra)
                                  ) * pieceCount;
                                return needed > 0;
                              })
                              .map(([productId, data], index) => {
                                const collapsed = isRowCollapsed(listId, lineId, productId);

                                const neededQty = Math.max(
                                  0,
                                  clampInt(data.quantity) -
                                    clampInt(data.onHand) +
                                    clampInt(data.extra)
                                );
                                const totalPieces = neededQty * (safeNumber(data.unit?.pieceCount, 1) || 1);

                                const boards =
                                  getItemByProductRef(productId).piecePerBoard
                                    ? Math.ceil(getBoardCount(productId, totalPieces))
                                    : null;
                                const racks =
                                  getItemByProductRef(productId).piecePerBoard &&
                                  getItemByProductRef(productId).boardPerRack
                                    ? Math.ceil(getRackCount(productId, boards))
                                    : null;

                                return (
                                  <li key={productId} style={{ marginBottom: 10 }}>
                                    <button
                                      type="button"
                                      onClick={() => toggleRowCollapse(listId, lineId, productId)}
                                      style={{
                                        marginRight: 8,
                                        border: "1px solid rgba(0,0,0,0.15)",
                                        borderRadius: 6,
                                        padding: "2px 8px",
                                        cursor: "pointer",
                                        background: "white",
                                      }}
                                      aria-label={collapsed ? "Expand details" : "Collapse details"}
                                      title={collapsed ? "Expand" : "Collapse"}
                                    >
                                      {collapsed ? "▶" : "▼"}
                                    </button>

                                    <span style={{ fontWeight: 600 }}>
                                      {getItemByProductRef(productId).name}
                                    </span>
                                    {": "}
                                    {neededQty} {data.unit?.name ?? "Piece"} (×{safeNumber(data.unit?.pieceCount, 1) || 1})
                                    {" — "}
                                    <span style={{ opacity: 0.85 }}>
                                      Total pieces: {totalPieces}
                                      {boards !== null ? ` · Boards: ${boards}` : ""}
                                      {racks !== null ? ` · Racks: ${racks}` : ""}
                                    </span>

                                    {index > 0 ? (
                                      <ArrowCircleUpIcon
                                        onClick={() => moveProductUp(listId, lineId, productId)}
                                        className="icon"
                                      />
                                    ) : null}
                                    {index + 1 < Object.entries(products || {}).length ? (
                                      <ArrowCircleDownIcon
                                        onClick={() => moveProductDown(listId, lineId, productId)}
                                        className="icon"
                                      />
                                    ) : null}

                                    {!collapsed && (
                                      <ul>
                                        {Object.entries(
                                          getItemByProductRef(productId).ingredients || {}
                                        ).map(([idx, ingredient]) => (
                                          <li key={idx}>
                                            <strong>
                                              {getItemByProductRef(Object.keys(ingredient)[0]).name}
                                              {" — Total: "}
                                              {Math.round(
                                                (calculateTotal(ingredient, data, lineId, productId) /
                                                  safeNumber(MASS_UNITS?.[0]?.amount, 1)) *
                                                  100
                                              ) / 100}{" "}
                                              {MASS_UNITS[0].abbreviation}{" "}
                                            </strong>
                                            {calculateScale(ingredient, data)}
                                            {calculateScrap(ingredient, data, lineId, productId)}
                                            {Object.keys(ingredient)[0].startsWith("doughs/") ? (
                                              <>
                                                <br />
                                                {calculateMixes(ingredient, data, lineId, productId).mixes}{" "}
                                                Mixes{" "}
                                                {data.rework ? (
                                                  <>
                                                    {calculateMixes(ingredient, data, lineId, productId).reworkMixes}{" "}
                                                    Rework Mixes{" "}
                                                  </>
                                                ) : null}
                                                {" @ "}
                                                {safeNumber(getItemByProductRef(Object.keys(ingredient)[0]).maxMix, 0) /
                                                  safeNumber(MASS_UNITS?.[0]?.amount, 1)}
                                                {MASS_UNITS[0].abbreviation}
                                              </>
                                            ) : null}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </li>
                                );
                              })}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              )}
            </div>

            <div style={{ padding: "0 16px" }}>
              <h3>Boards & Racks</h3>
              <table>
                <thead>
                  <tr>
                    <th>Boards</th>
                    <th>Racks</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{totalBoards}</td>
                    <td>{totalRacks}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {Object.keys(ingredientTotals).length > 0 && (
              <div style={{ padding: "0 16px" }}>
                <h3>Total Ingredient Consumption</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Ingredient</th>
                      <th>Total Needed {MASS_UNITS[0].abbreviation}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(ingredientTotals).map(([ingredientId, total]) => {
                      const ingredient = ingredients.find((i) => i.id === ingredientId);
                      const denom = safeNumber(MASS_UNITS?.[0]?.amount, 1) || 1;
                      const v =
                        Math.round(((safeNumber(total, 0).toFixed(2) / denom) * 100)) /
                        100;

                      return (
                        <tr key={ingredientId}>
                          <td>{ingredient ? ingredient.name : ingredientId}</td>
                          <td>{Number.isFinite(v) ? v : 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ padding: "0 16px" }}>
              <h3>Mixer Chart</h3>
              {generateMixerChartTable()}
            </div>
          </>
        ) : null}

        <div className="productionPlannerCard">
          <div className="productionPlannerHeader">
            <h3>Production Baking Timing</h3>
            <p>Plan queued batches, assign ovens, and estimate bake completion times.</p>
          </div>

          <div className="productionPlannerControls">
            <label htmlFor="production-start-time">Schedule Start</label>
            <input
              id="production-start-time"
              className="ckInput"
              type="time"
              value={documentData.productionStartTime || "06:00"}
              onChange={(e) =>
                setDocumentData((prev) => ({
                  ...prev,
                  productionStartTime: e.target.value || "06:00",
                }))
              }
            />
            <button type="button" className="ckButton" onClick={addQueueBatch}>
              Add Batch
            </button>
            <button type="button" className="ckButton" onClick={clearManualOvenAssignments}>
              Auto-Assign All
            </button>
            {/*
            <ReactToPrint
              trigger={() => (
                <button type="button" className="ckButton">
                  Print Oven
                </button>
              )}
              content={() => ovenTimingComponentRef.current}
            />
            */}
          </div>

          <div ref={ovenTimingComponentRef}>
            <div className="productionPlannerTableWrap">
              <table className="productionPlannerTable">
                <thead>
                  <tr>
                    <th>Priority</th>
                    <th>Bread Type</th>
                    <th>Quantity</th>
                    <th>Manual Oven (optional)</th>
                    <th>Assigned Oven</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Duration</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(documentData.productionQueue || []).map((batch) => {
                    const scheduleItem =
                      productionSchedule.items.find((item) => item.id === batch.id) || null;
                    const compatibleOvens = ovens.filter(
                      (oven) => getOvenEffectiveRate(oven, batch.productRef) > 0
                    );
                    return (
                      <tr key={batch.id}>
                        <td>
                          <input
                            className="ckInput"
                            type="number"
                            min={0}
                            value={batch.priority ?? 0}
                            onChange={(e) => updateQueueBatch(batch.id, "priority", e.target.value)}
                          />
                        </td>
                        <td>
                          <select
                            className="ckSelect"
                            value={batch.productRef || ""}
                            onChange={(e) => updateQueueBatch(batch.id, "productRef", e.target.value)}
                          >
                            <option value="">Select product</option>
                            {[...products]
                              .sort((a, b) =>
                                (a.name || "").localeCompare(b.name || "")
                              )
                              .map((product) => (
                                <option key={product.id} value={product.id}>
                                  {product.name}
                                </option>
                              ))}
                          </select>
                        </td>
                        <td>
                          <input
                            className="ckInput"
                            type="number"
                            min={0}
                            value={batch.quantity ?? 0}
                            onChange={(e) => updateQueueBatch(batch.id, "quantity", e.target.value)}
                          />
                        </td>
                        <td>
                          <select
                            className="ckSelect"
                            value={batch.manualOvenId || ""}
                            onChange={(e) => updateQueueBatch(batch.id, "manualOvenId", e.target.value)}
                          >
                            <option value="">Auto</option>
                            {compatibleOvens.map((oven) => (
                              <option key={oven.id} value={oven.id}>
                                {oven.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>{scheduleItem?.assignedOvenName || "Unassigned"}</td>
                        <td>{scheduleItem?.startTime || "-"}</td>
                        <td>{scheduleItem?.endTime || "-"}</td>
                        <td>{formatBakingMinutesLabel(scheduleItem?.durationMinutes || 0)}</td>
                        <td>
                          <button
                            type="button"
                            className="ckButton"
                            onClick={() => removeQueueBatch(batch.id)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="productionPlannerMetrics">
              <div className="metricItem">
                <strong>Total queued pieces</strong>
                <span>{productionSchedule.metrics.totalPieces}</span>
              </div>
              <div className="metricItem">
                <strong>Total bake window</strong>
                <span>{formatBakingMinutesLabel(productionSchedule.metrics.totalMinutes)}</span>
              </div>
              <div className="metricItem">
                <strong>Overall throughput</strong>
                <span>{productionSchedule.metrics.overallThroughputPerHour} pieces/hour</span>
              </div>
            </div>

            {productionSchedule.metrics.ovens.length > 0 && (
              <div className="productionPlannerUtilization">
                <h4>Oven Utilization</h4>
                <table className="productionPlannerTable">
                  <thead>
                    <tr>
                      <th>Oven</th>
                      <th>Batches</th>
                      <th>Busy</th>
                      <th>Utilization</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productionSchedule.metrics.ovens.map((oven) => (
                      <tr key={oven.ovenId}>
                        <td>{oven.ovenName}</td>
                        <td>{oven.itemCount}</td>
                        <td>{formatBakingMinutesLabel(oven.busyMinutes)}</td>
                        <td>{oven.utilizationPercent}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default Production;
